import axios from 'axios';
import { getCachedResult, setCachedResult } from '../utils/cache';

// 환경변수에서 API 키를 불러옵니다. (예: .env 파일에 REACT_APP_OTJ_API_KEY)
const API_KEY = process.env.REACT_APP_OTJ_API_KEY || 'temporary_test_key_123';
// BASE_URL을 환경변수 OTJ_API_BASE_URL에서 가져오도록 수정
const BASE_URL = process.env.REACT_APP_OTJ_API_BASE_URL || 'https://otj-prod-apim.azure-api.net/otj/v1';

// 임시: 실제 API 키 사용 (테스트용)
// 환경변수를 우선 사용, 없으면 하드코딩된 키 사용
const ACTUAL_API_KEY = process.env.REACT_APP_OTJ_API_KEY || '44ff38bcae934b375cc83';

console.log('[otjApi] API_KEY 설정 상태:', {
  hasApiKey: !!API_KEY,
  apiKeyLength: API_KEY ? API_KEY.length : 0,
  baseUrl: BASE_URL,
  envApiKey: process.env.REACT_APP_OTJ_API_KEY,
  envApiKeyLength: process.env.REACT_APP_OTJ_API_KEY ? process.env.REACT_APP_OTJ_API_KEY.length : 0,
  actualApiKey: API_KEY,
  actualApiKeyValue: ACTUAL_API_KEY,
  usingActualKey: !!ACTUAL_API_KEY,
  finalApiKey: ACTUAL_API_KEY !== 'YOUR_ACTUAL_API_KEY_HERE' ? ACTUAL_API_KEY : (API_KEY || 'test-key')
});

// 임시 테스트: API 키가 없어도 요청 시도
console.log('[otjApi] 주의: API 키 상태에 관계없이 요청을 시도합니다');

const otjApi = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Ocp-Apim-Subscription-Key': ACTUAL_API_KEY !== 'YOUR_ACTUAL_API_KEY_HERE' ? ACTUAL_API_KEY : (API_KEY || 'test-key'),
    'Content-Type': 'application/json',
  },
});

// GUID 생성 함수
const generateGuid = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// 기본 게스트 API 키 (테스트용 여러 옵션)
const DEFAULT_GUEST_API_KEY = 'ec7f8f04-1fd4-4061-b5ad-0ee9f1626884';

// 게스트 키 테스트 옵션들
const GUEST_KEY_OPTIONS = {
  CURRENT: 'ec7f8f04-1fd4-4061-b5ad-0ee9f1626884',
  NONE: null,  // 게스트 키 없이 요청
  TEST1: 'guest',  // 단순 문자열 테스트
  TEST2: '00000000-0000-0000-0000-000000000000'  // 기본 UUID 형식
};

// 현재 사용할 게스트 키 옵션 (테스트용)
const CURRENT_GUEST_KEY_OPTION = 'CURRENT';  // CURRENT, NONE, TEST1, TEST2 중 선택

console.log('[otjApi] DEFAULT_GUEST_API_KEY:', DEFAULT_GUEST_API_KEY);

// API 키를 가져오는 함수
const getApiKey = () => {
  const apiKey = ACTUAL_API_KEY !== 'YOUR_ACTUAL_API_KEY_HERE' ? ACTUAL_API_KEY : (API_KEY || 'test-key');
  console.log('[otjApi] getApiKey 반환값:', apiKey ? `${apiKey.substring(0, 8)}...` : 'null');
  return apiKey;
};

// X-User-ApiKey 헤더를 추가하는 함수
const createApiInstanceWithUserKey = (userApiKey) => {
  console.log('[otjApi] createApiInstanceWithUserKey 호출됨, userApiKey:', !!userApiKey);
  
  // 테스트용 게스트 키 선택
  const selectedGuestKey = GUEST_KEY_OPTIONS[CURRENT_GUEST_KEY_OPTION];
  const finalUserApiKey = userApiKey || selectedGuestKey;
  
  console.log('[otjApi] 게스트 키 테스트 모드:', CURRENT_GUEST_KEY_OPTION);
  console.log('[otjApi] 선택된 게스트 키:', selectedGuestKey ? 
    `${selectedGuestKey.substring(0, 8)}...` : 'null');
  console.log('[otjApi] 최종 사용될 User API Key:', finalUserApiKey ? 
    `${finalUserApiKey.substring(0, 8)}...` : 'null');
  
  const apiInstance = axios.create({
    baseURL: BASE_URL,
    headers: {
      'Content-Type': 'application/json',
      'Ocp-Apim-Subscription-Key': getApiKey(),
    },
  });

  // X-User-ApiKey 헤더 추가 (null이 아닌 경우에만)
  if (finalUserApiKey) {
    apiInstance.defaults.headers['X-User-ApiKey'] = finalUserApiKey;
    console.log('[otjApi] X-User-ApiKey 헤더 추가됨');
  } else {
    console.log('[otjApi] X-User-ApiKey 헤더 추가하지 않음 (NONE 테스트)');
  }

  return apiInstance;
};

// 교정 요청 (proofread) - X-User-ApiKey 헤더 포함, 캐시 지원
export const proofread = async ({ text, includereasons = true, language = 'kr', userApiKey }) => {
  console.log('[otjApi] proofread 함수 진입:', { text, includereasons, language, userApiKey: !!userApiKey });
  
  // 캐시 확인
  const cachedResult = getCachedResult(text, 'proofread', includereasons, language, userApiKey);
  if (cachedResult) {
    console.log('[otjApi] 캐시된 결과 반환');
    return {
      status: 200,
      statusText: 'OK (Cached)',
      data: cachedResult
    };
  }
  
  try {
    const apiInstance = createApiInstanceWithUserKey(userApiKey);
    console.log('[otjApi] API 인스턴스 생성 완료');
    
    // 실제 전송될 헤더들 확인
    console.log('[otjApi] 실제 전송 헤더들:', {
      'Ocp-Apim-Subscription-Key': apiInstance.defaults.headers['Ocp-Apim-Subscription-Key'] ? 
        `${apiInstance.defaults.headers['Ocp-Apim-Subscription-Key'].substring(0, 8)}...` : 'null',
      'X-User-ApiKey': apiInstance.defaults.headers['X-User-ApiKey'] ? 
        `${apiInstance.defaults.headers['X-User-ApiKey'].substring(0, 8)}...` : 'null',
      'Content-Type': apiInstance.defaults.headers['Content-Type']
    });
    
    const requestData = { text, includereasons, language };
    console.log('[otjApi] 요청 데이터:', requestData);
    console.log('[otjApi] 요청 URL:', `${BASE_URL}/proofread`);
    console.log('[otjApi] POST 요청 시작 - /proofread');
    
    const response = await apiInstance.post('/proofread', requestData);
    
    console.log('[otjApi] POST 요청 완료:', { 
      status: response.status, 
      statusText: response.statusText,
      dataLength: JSON.stringify(response.data).length 
    });
    
    // 성공한 결과를 캐시에 저장
    if (response.status === 200) {
      setCachedResult(text, 'proofread', includereasons, language, userApiKey, response.data);
    }
    
    return response;
  } catch (error) {
    console.error('[otjApi] proofread 함수 에러:', error);
    
    // 더 상세한 401 오류 정보
    if (error.response?.status === 401) {
      console.error('[otjApi] 401 인증 오류 상세 정보:');
      console.error('- Status:', error.response.status);
      console.error('- StatusText:', error.response.statusText);  
      console.error('- Response Headers:', error.response.headers);
      console.error('- Response Data:', error.response.data);
      console.error('- Request URL:', error.config?.url);
      console.error('- Request Headers:', error.config?.headers);
    }
    
    console.error('[otjApi] 에러 상세:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      headers: error.response?.headers
    });
    
    throw error;
  }
};

// 카피에딧 요청 (copyedit) - X-User-ApiKey 헤더 포함, 캐시 지원
export const copyedit = async ({ text, includereasons = true, language = 'kr', userApiKey }) => {
  console.log('[otjApi] copyedit 함수 진입:', { text, includereasons, language, userApiKey: !!userApiKey });
  
  // 캐시 확인
  const cachedResult = getCachedResult(text, 'copyedit', includereasons, language, userApiKey);
  if (cachedResult) {
    console.log('[otjApi] 캐시된 결과 반환');
    return {
      status: 200,
      statusText: 'OK (Cached)',
      data: cachedResult
    };
  }
  
  try {
    const apiInstance = createApiInstanceWithUserKey(userApiKey);
    console.log('[otjApi] copyedit API 요청 시작');
    
    const response = await apiInstance.post('/copyedit', { text, includereasons, language });
    
    console.log('[otjApi] copyedit 요청 완료:', { 
      status: response.status, 
      statusText: response.statusText 
    });
    
    // 성공한 결과를 캐시에 저장
    if (response.status === 200) {
      setCachedResult(text, 'copyedit', includereasons, language, userApiKey, response.data);
    }
    
    return response;
  } catch (error) {
    console.error('[otjApi] copyedit 함수 에러:', error);
    throw error;
  }
};

// 회원 등록 (registeruser)
export const registerUser = async (userData) => {
  return otjApi.post('/registeruser', userData);
};

// 회원 정보 조회 (getuser)
export const getUser = async ({ provider, provideruserid }) => {
  return otjApi.get('/getuser', { params: { provider, provideruserid } });
};

// 유저 프로필 조회 (getuserprofile)
export const getUserProfile = async ({ apikey }) => {
  return otjApi.get('/getuserprofile', { params: { apikey } });
};

// 크레딧 조회 (getcredits)
export const getCredits = async ({ apikey }) => {
  console.log('[otjApi] getCredits 함수 진입:', { apikey: !!apikey });
  
  try {
    const response = await otjApi.get('/getcredits', { params: { apikey } });
    
    console.log('[otjApi] getCredits 요청 완료:', { 
      status: response.status, 
      statusText: response.statusText,
      data: response.data
    });
    
    return response;
  } catch (error) {
    console.error('[otjApi] getCredits 함수 에러:', error);
    throw error;
  }
};

// 사용자 인증 및 등록 로직
export const authenticateUser = async (auth0User) => {
  const maxRetries = 3;
  let retryCount = 0;

  while (retryCount < maxRetries) {
    try {
      // 1. 기존 사용자 확인
      const provider = auth0User.sub.split('|')[0]; // google, facebook 등
      const providerUserId = auth0User.sub.split('|')[1];
      
      const getUserResponse = await getUser({ provider, provideruserid: providerUserId });
      
      if (getUserResponse.status === 200) {
        // 기존 사용자 발견
        const userData = getUserResponse.data;
        return {
          success: true,
          user: {
            displayName: userData.displayName,
            email: userData.email,
            primaryKey: userData.primaryKey,
            remainingCredits: userData.remainingCredits
          }
        };
      }
    } catch (error) {
      // 404 또는 다른 오류 - 새 사용자 등록 시도
      try {
        const provider = auth0User.sub.split('|')[0];
        const providerUserId = auth0User.sub.split('|')[1];
        const primaryKey = generateGuid();
        const secondaryKey = generateGuid();

        const registerData = {
          provider,
          provideruserid: providerUserId,
          email: auth0User.email,
          displayname: auth0User.name,
          primarykey: primaryKey,
          secondarykey: secondaryKey
        };

        const registerResponse = await registerUser(registerData);
        
        if (registerResponse.status === 200) {
          // 등록 성공 후 다시 사용자 정보 조회
          const getUserResponse = await getUser({ provider, provideruserid: providerUserId });
          
          if (getUserResponse.status === 200) {
            const userData = getUserResponse.data;
            return {
              success: true,
              user: {
                displayName: userData.displayName,
                email: userData.email,
                primaryKey: userData.primaryKey,
                remainingCredits: userData.remainingCredits
              }
            };
          }
        }
      } catch (registerError) {
        console.error('사용자 등록 실패:', registerError);
        retryCount++;
        
        if (retryCount >= maxRetries) {
          return {
            success: false,
            error: '오탈자닷컴의 일시적인 장애가 발생했습니다. 잠시 후 다시 시도해주세요.'
          };
        }
        
        // 잠시 대기 후 재시도
        await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
      }
    }
  }

  return {
    success: false,
    error: '오탈자닷컴의 일시적인 장애가 발생했습니다. 잠시 후 다시 시도해주세요.'
  };
};

// 회원 정보 수정 (updateuser) - X-User-ApiKey 헤더 필요
export const updateUser = async ({ userApiKey, provider, provideruserid, email }) => {
  const apiInstance = createApiInstanceWithUserKey(userApiKey);
  return apiInstance.post('/updateuser', { provider, provideruserid, email });
};

// 기타 엔드포인트도 필요시 추가 가능합니다.

export default {
  proofread,
  copyedit,
  registerUser,
  getUser,
  getUserProfile,
  getCredits,
  authenticateUser,
  updateUser,
}; 